import { Chip } from "components/DesignSystem/Chips/Chips";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Filter } from "components/DesignSystem/Filter/Filter";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { DateFilter } from "components/Filters/DateFilter";
import { MultiGroupFilter } from "components/Filters/MultiGroupFilter";
import { DISPLAY_DATE } from "constants/date";
import {
  AUDIT_LOG_SOURCE_OPTIONS,
  AUDIT_LOG_FEATURE_OPTIONS,
} from "constants/auditLogFilters";
import dayjs from "dayjs";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";

type FilterValues = {
  START_DATE: string;
  END_DATE: string;
  SELECT_PERIOD: string;
  GROUPS: string[];
  ENDITY_ID: string;
  FEATURE: string[];
  SOURCE: string;
};

type AuditLogFilterProps = {
  filterValues: FilterValues;
  updateFilter: <K extends keyof FilterValues>(
    name: K,
    value: FilterValues[K]
  ) => void;
  isSingleGroupView: boolean;
};

export const AuditLogFilter = ({
  filterValues,
  updateFilter,
  isSingleGroupView,
}: AuditLogFilterProps) => {
  const group = useCurrentGroupContext();

  const selectedFeatures = filterValues.FEATURE || [];

  const toggleFeature = (value: string) => {
    const updated = selectedFeatures.includes(value)
      ? selectedFeatures.filter((v) => v !== value)
      : [...selectedFeatures, value];
    updateFilter("FEATURE", updated);
  };

  const capsule = (
    <>
      {Boolean(filterValues.START_DATE) && (
        <Chip
          onRemove={() => {
            updateFilter("START_DATE", "");
            updateFilter("SELECT_PERIOD", "");
          }}
          isActive
          filterType="DATE"
        >
          From {dayjs(filterValues.START_DATE).format(DISPLAY_DATE)}
        </Chip>
      )}
      {Boolean(filterValues.END_DATE) && (
        <Chip
          onRemove={() => {
            updateFilter("END_DATE", "");
            updateFilter("SELECT_PERIOD", "");
          }}
          isActive
          filterType="DATE"
        >
          To {dayjs(filterValues.END_DATE).format(DISPLAY_DATE)}
        </Chip>
      )}
      {Boolean(filterValues.ENDITY_ID) && (
        <Chip
          onRemove={() => updateFilter("ENDITY_ID", "")}
          isActive
          filterType="ENTITY_ID"
        >
          Entity:{" "}
          {
            group?.entities?.find(
              (entity) => entity.uuid === filterValues.ENDITY_ID
            )?.name
          }
        </Chip>
      )}
      {(filterValues.GROUPS?.length ?? 0) > 0 && (
        <Chip
          onRemove={() => updateFilter("GROUPS", [])}
          isActive
          filterType="GROUP"
        >
          Groups ({filterValues.GROUPS?.length})
        </Chip>
      )}
      {selectedFeatures.length > 0 && (
        <Chip
          onRemove={() => updateFilter("FEATURE", [])}
          isActive
          filterType="FEATURE"
        >
          Features ({selectedFeatures.length})
        </Chip>
      )}
      {Boolean(filterValues.SOURCE) && (
        <Chip
          onRemove={() => updateFilter("SOURCE", "")}
          isActive
          filterType="SOURCE"
        >
          {
            AUDIT_LOG_SOURCE_OPTIONS.find(
              (o) => o.value === filterValues.SOURCE
            )?.label
          }
        </Chip>
      )}
    </>
  );

  return (
    <Filter.Root
      defaultValue={isSingleGroupView ? "ENTITY_ID" : "GROUP"}
      capsule={capsule}
    >
      <Filter.Portal>
        <Filter.List>
          {!isSingleGroupView && (
            <Filter.ListItem value="GROUP">Company group</Filter.ListItem>
          )}
          {isSingleGroupView && (
            <Filter.ListItem value="ENTITY_ID">Entity</Filter.ListItem>
          )}
          <Filter.ListItem value="DATE">Date</Filter.ListItem>
          <Filter.ListItem value="FEATURE">Feature</Filter.ListItem>
          <Filter.ListItem value="SOURCE">Source</Filter.ListItem>
        </Filter.List>

        <Filter.Body value="GROUP" block>
          <MultiGroupFilter values={filterValues} updateFilter={updateFilter} />
        </Filter.Body>

        <Filter.Body value="ENTITY_ID" block>
          <RadioGroup.Root
            value={filterValues.ENDITY_ID}
            className="t-flex t-flex-col t-gap-3"
            onValueChange={(value) => updateFilter("ENDITY_ID", value)}
          >
            {group?.entities?.map((entity) => (
              <RadioGroup.Item
                key={entity.uuid}
                value={entity.uuid}
                align="start"
                inline={false}
              >
                {entity.name}
              </RadioGroup.Item>
            ))}
          </RadioGroup.Root>
        </Filter.Body>

        <Filter.Body value="DATE" block>
          <DateFilter values={filterValues} updateFilter={updateFilter} />
        </Filter.Body>

        <Filter.Body value="FEATURE" block>
          <div className="t-flex t-flex-col t-gap-3">
            {AUDIT_LOG_FEATURE_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="t-flex t-items-center t-gap-2 t-cursor-pointer t-select-none t-text-body-sm"
              >
                <Checkbox
                  checked={selectedFeatures.includes(option.value)}
                  onChange={() => toggleFeature(option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </Filter.Body>

        <Filter.Body value="SOURCE" block>
          <RadioGroup.Root
            value={filterValues.SOURCE}
            className="t-flex t-flex-col t-gap-3"
            onValueChange={(value) => updateFilter("SOURCE", value)}
          >
            {AUDIT_LOG_SOURCE_OPTIONS.map((option) => (
              <RadioGroup.Item key={option.value} value={option.value}>
                {option.label}
              </RadioGroup.Item>
            ))}
          </RadioGroup.Root>
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};
